import { inject } from '@angular/core';
import { CanDeactivateFn } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { firstValueFrom, map } from 'rxjs';

import { PlanEditSessionActions as Session } from '@core/store/plan-edit/plan-edit-session.actions';
import {
  selectPlanEditSession,
  unsavedEditCount
} from '@core/store/plan-edit/plan-edit-session.selectors';

import { PlanEditLeavePromptService } from '@features/components/plan/edit/plan-edit-dialogs/plan-edit-leave-prompt.service';

/**
 * Leaving the plan ends this tab's edit session on it: an open session would keep the plan locked,
 * and the heartbeat of an active user would keep it open. Unsaved edits are asked about first.
 */
export const planEditLeaveGuard: CanDeactivateFn<unknown> = async (_component, route) => {
  const store = inject(Store);
  const actions$ = inject(Actions);
  const prompt = inject(PlanEditLeavePromptService);
  const session = store.selectSignal(selectPlanEditSession);
  const { mode, planId } = session();
  if (mode === 'off' || planId !== route.paramMap.get('planId')) return true;

  if (unsavedEditCount(session()) === 0) {
    store.dispatch(Session.doneRequested());
    return true;
  }

  const answer = await prompt.ask();
  if (answer === 'stay') return false;
  if (answer === 'discard') {
    store.dispatch(Session.discardRequested());
    return true;
  }
  // Only an open session can save, or is already saving; any other would never answer.
  const modeNow = session().mode;
  if (modeNow !== 'editing' && modeNow !== 'saving') return false;

  // Subscribed before the dispatch: the effect answers a save the socket cannot send synchronously.
  const outcome = firstValueFrom(
    actions$.pipe(
      ofType(Session.saved, Session.saveRejected, Session.closed, Session.connectionLost),
      map((action) => action.type === Session.saved.type)
    )
  );
  store.dispatch(Session.saveRequested());
  if (!(await outcome)) return false;
  store.dispatch(Session.doneRequested());
  return true;
};
